import { DataUsage } from '@/shared/types/serviceLineTypes';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Card, CardContent } from '@/shared/ui/card';
import { DataUsageBarChart } from '@/shared/components/DataUsageBarChart';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { formatDateToLocaleString } from '@/shared/utils/date-utils';
import { useEffect, useMemo, useState } from 'react';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from '@/shared/ui/dropdown-menu';
import { useSendAccountUsagesReport } from '@/domains/accounts/services/account-usages';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';

interface DatapoolConsumptionChartDialogProps {
    organizationId: string | undefined;
    dataUsages: DataUsage[];
    regionCode: string | undefined;
    isOpen: boolean;
    onClose: () => void;
}

export function DatapoolConsumptionChartDialog({
    organizationId,
    dataUsages,
    regionCode,
    isOpen,
    onClose,
}: DatapoolConsumptionChartDialogProps) {
    const billingPeriods: { billingPeriod: string, label: string }[] = useMemo(() => {
        const months = dataUsages.map(usage => usage.startDate);
        const uniqueMonths = [...new Set(months)];
        const sortedMonths = uniqueMonths.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        return sortedMonths.reverse().slice(1, 6).map((month) => ({
            billingPeriod: month,
            label: formatDateToLocaleString(month, { year: 'numeric', month: 'long' }),
        }));
    }, [dataUsages]);

    const [selectedBillingPeriods, setSelectedBillingPeriods] = useState<string[]>(billingPeriods.map((billingPeriod) => billingPeriod.billingPeriod));
    const sendAccountUsageEmail = useSendAccountUsagesReport();
    const allMonthsSelected = billingPeriods.length > 0 && selectedBillingPeriods.length === billingPeriods.length;

    useEffect(() => {
        setSelectedBillingPeriods((prevSelectedBillingPeriods) => {
            const availableBillingPeriods = new Set(billingPeriods.map((billingPeriod) => billingPeriod.billingPeriod));
            const nextSelectedBillingPeriods = prevSelectedBillingPeriods.filter((billingPeriod) => availableBillingPeriods.has(billingPeriod));
            return nextSelectedBillingPeriods.length > 0
                ? nextSelectedBillingPeriods
                : billingPeriods.map((billingPeriod) => billingPeriod.billingPeriod);
        });
    }, [billingPeriods]);

    const handleToggleAllBillingPeriods = () => {
        setSelectedBillingPeriods((prevSelectedBillingPeriods) => (
            prevSelectedBillingPeriods.length === billingPeriods.length
                ? []
                : billingPeriods.map((billingPeriod) => billingPeriod.billingPeriod)
        ));
    };

    const handleToggleBillingPeriod = (billingPeriod: string, checked: boolean | 'indeterminate') => {
        const isChecked = checked === true;
        setSelectedBillingPeriods((prevSelectedBillingPeriods) => {
            if (isChecked) {
                return prevSelectedBillingPeriods.includes(billingPeriod)
                    ? prevSelectedBillingPeriods
                    : [...prevSelectedBillingPeriods, billingPeriod];
            }
            return prevSelectedBillingPeriods.filter((selectedBillingPeriod) => selectedBillingPeriod !== billingPeriod);
        });
    };

    const handleSendAccountUsageEmail = async () => {
        if (!organizationId) { return; }
        try {
            const months = selectedBillingPeriods.length > 0
                ? selectedBillingPeriods
                : billingPeriods.map((billingPeriod) => billingPeriod.billingPeriod);
            const monthsString = months.sort((a, b) => a.localeCompare(b)).reverse().map(month => formatDateToLocaleString(month, { year: 'numeric', month: 'short' }).split(" ").join('-')).join(','); //Apr-2026, May-2026, etc.
            await sendAccountUsageEmail.mutateAsync({ months: monthsString });
            showSuccess(SUCCESS.ACCOUNT.SENT_USAGE_EMAIL);
        } catch (error) {
            showErrorWithApiError(error, ERROR.ACCOUNT.SENT_USAGE_EMAIL);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-7xl max-h-[95vh] flex flex-col gap-4 overflow-hidden p-4">
                <DialogHeader className="shrink-0">
                    <DialogTitle className="flex items-center gap-2">
                        <span className="text-lg font-medium">
                            Data Consumptions by Month
                            {regionCode && (
                                <span className="font-bold text-blue-500"> — {regionCode}</span>
                            )}
                        </span>
                    </DialogTitle>

                    {organizationId !== undefined && dataUsages.length > 1 && (
                        <div className="flex items-center justify-between w-full">
                            <h2 className="text-md font-medium">Select months for usage reports</h2>
                            <div className="flex items-center gap-2">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="outline" className="w-48 justify-between">
                                            {allMonthsSelected
                                                ? 'All Months'
                                                : selectedBillingPeriods.length > 0
                                                    ? `${selectedBillingPeriods.length} month${selectedBillingPeriods.length === 1 ? '' : 's'} selected`
                                                    : 'Select months'}
                                            <ChevronDown className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56">
                                        <DropdownMenuCheckboxItem
                                            checked={allMonthsSelected}
                                            onSelect={(event) => event.preventDefault()}
                                            onCheckedChange={handleToggleAllBillingPeriods}
                                        >
                                            All Months
                                        </DropdownMenuCheckboxItem>
                                        {billingPeriods.map((billingPeriod) => (
                                            <DropdownMenuCheckboxItem
                                                key={billingPeriod.billingPeriod}
                                                checked={selectedBillingPeriods.includes(billingPeriod.billingPeriod)}
                                                onSelect={(event) => event.preventDefault()}
                                                onCheckedChange={(checked) => handleToggleBillingPeriod(billingPeriod.billingPeriod, checked)}
                                            >
                                                {billingPeriod.label}
                                            </DropdownMenuCheckboxItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                <Button className="gap-2" onClick={handleSendAccountUsageEmail} disabled={selectedBillingPeriods.length === 0}>
                                    {sendAccountUsageEmail.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                                    {sendAccountUsageEmail.isPending ? 'Submitting...' : 'Submit'}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogHeader>

                <div className="space-y-6">
                    {dataUsages.length > 0 ? (
                        <Card>
                            <CardContent>
                                <DataUsageBarChart dataUsages={dataUsages} />
                            </CardContent>
                        </Card>
                    ) : (
                        <Card>
                            <CardContent className="flex items-center justify-center h-64">
                                <div className="text-center">
                                    No consumption data available to display.
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
